import type { ControlBarProps } from "./ControlBar.types";
import { getControlBarStyles } from "./ControlBar.styles";
import { Icon, Text } from "@/components/atoms";
import { cn } from "@/utils/cn";

export const ControlBar = ({
  tabs,
  currentTab,
  onTabChange,
  children,
  height,
  isControlBarOpened = true,
  showClearFilters,
  onClearFilters,
}: ControlBarProps) => {
  const isContentOpen = tabs.some((tab) => tab.id === currentTab);

  const styles = getControlBarStyles({ isControlBarOpened, hasFixedHeight: height !== undefined });

  return (
    <div className="relative w-full">
      <div
        className={styles.container}
        style={isControlBarOpened && height !== undefined ? { height: height + "px" } : {}}
      >
        <div className={styles.tabsWrapper}>
          {tabs.map((tab) => {
            const isSelected = tab.id === currentTab;
            const isActive = tab.isActiveOrApplied || isSelected;
            const counter = tab.counter;

            return (
              <div
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={styles.tabButton({ isSelected, isActive, isControlBarOpened, counter })}
              >
                <span
                  className={cn("text-dark-100 flex items-center gap-1 transition-all", {
                    "text-primary-100": isSelected,
                    "text-primary": !isSelected && counter > 0,
                  })}
                >
                  {tab.iconName && (
                    <Icon
                      color={
                        (!isSelected && counter > 0) ||
                        (isSelected && counter > 0 && !isControlBarOpened)
                          ? "primary"
                          : isSelected
                            ? "primary-100"
                            : "dark-100"
                      }
                      name={tab.iconName}
                    />
                  )}
                  <Text
                    className={cn("text-dark-100 transition-all", {
                      "text-primary-100": isSelected && isControlBarOpened,
                      "text-primary":
                        (!isSelected && counter > 0) ||
                        (isSelected && counter > 0 && !isControlBarOpened),
                    })}
                    variant="button"
                  >
                    {tab.label}
                  </Text>
                  {tab.counter !== undefined && tab.counter > 0 && (
                    <span
                      className={cn(
                        "text-primary-100 bg-primary-400 rounded-full flex items-center justify-center w-[18px] h-[18px]",
                        {
                          "bg-transparent text-primary border border-primary":
                            (!isSelected && counter > 0) ||
                            (isSelected && counter > 0 && !isControlBarOpened),
                        }
                      )}
                    >
                      <Text variant="label">{tab.counter}</Text>
                    </span>
                  )}
                </span>
              </div>
            );
          })}

          {showClearFilters && (
            <div
              onClick={onClearFilters}
              className={styles.clearButton}
              title="Limpar filtros"
            >
              <Icon name="clear-alt" />
            </div>
          )}
        </div>

        {isControlBarOpened && (
          <section
            data-state="open"
            className={styles.contentSection}
          >
            {children}
          </section>
        )}
      </div>
    </div>
  );
};
